import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { debounce } from "lodash";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { useGetSentimentsBreakdownMutation } from "../../../store/api/lumi/sentiment";
import moment from "moment/moment";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import LabelSelector from "./LabelSelector";
import SmallMultiple from "./SmallMultiple";

export function SentimentOverTimeLineGraph(props) {
  const { id, filters, includeExamples, selectedDateName, interval, setFilterDocCount, widget, smoothLine } = props;
  
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [smallMultipleData, setSmallMultipleData] = useState({});
  const [defaultConcepts, setDefaultConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [getSentimentsBreakdown] = useGetSentimentsBreakdownMutation();
  const [conceptColors, setConceptColors] = useState({});
  const labelFontSize = useSelector((state) => {
    const fontSizes = state.fontManager.fontSizes;
    const normalizedKey = Object.keys(fontSizes).find((key) => key.trim() === "labelFontSize");
    return fontSizes[normalizedKey] || 16;
  });

  const [allLabels, setAllLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const conceptFilter = useMemo(() => getConceptFilter(props), [props]);

  const getSentimentData = useCallback(async () => {
    setLoading(true);
    const { data } = await getSentimentsBreakdown({
      id,
      concept_selector: conceptFilter,
      filter: filters,
      include_examples: includeExamples,
      breakdowns: [{ name: selectedDateName, interval }],
    });
    setFilterDocCount(data.filter_count);
    setData(data.breakdowns?.[0].buckets);
    const uniqueConcepts = data.match_counts.map((item) => item.name)
      .slice(0, conceptFilter.limit || 10);
    setDefaultConcepts(uniqueConcepts);
    setLoading(false);
  }, [getSentimentsBreakdown, id, filters, includeExamples, interval, selectedDateName, conceptFilter, setFilterDocCount]);

  useEffect(() => {
    setLoading(true);
    const debouncedGetSentiment = debounce(getSentimentData, 1000);
    debouncedGetSentiment();
    return () => {
      debouncedGetSentiment.cancel();
    };
  }, [getSentimentData]);

  useEffect(() => {
    if (data?.length) {
      const conceptColorsTemp = {};
      const preparedData = data.map(({ label, match_counts, filter_count }) => {
        if (!filter_count) return;
        const formattedDate = formatDateToUtc(label, props.interval);
        const dateData = { iteration: formattedDate };
        match_counts.forEach((item) => {
          if (item.color) conceptColorsTemp[item.name] = item.color;
          dateData[`${item.name}_effect`] = item.sentiment_share
            ? (
                item.sentiment_share.negative * -1 +
                item.sentiment_share.positive * 1
              )
            : 0;
        });
        return dateData;
      }).filter(v => v !== undefined);
      
      const sortedData = preparedData.sort((a, b) =>
        moment(a.iteration).isBefore(moment(b.iteration)) ? -1 : 1
      );
      setChartData(sortedData);
      setConceptColors(conceptColorsTemp);

      const labelSet = new Set();
      sortedData.forEach(entry => {
        if (entry.iteration) labelSet.add(entry.iteration);
      });
      const labels = Array.from(labelSet);
      labels.sort((a, b) => moment(a).isBefore(moment(b)) ? -1 : 1);
      setAllLabels(labels);
      setSelectedLabels(labels);
      
      const smData = {};
      const concepts = props.selectedLocalConcepts && props.selectedLocalConcepts.length
        ? props.selectedLocalConcepts
        : defaultConcepts;
      concepts.forEach((concept) => {
        const key = `${concept}_effect`;
        smData[concept] = sortedData.map((entry) => ({
          date: entry.iteration,
          [concept]: entry[key] !== undefined ? entry[key] : null,
        }));
      });
      setSmallMultipleData(smData);
    }
  }, [data, props.interval, props.selectedLocalConcepts, defaultConcepts]);

  const handleToggleLabel = (label) => {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const filteredSmallMultipleData = {};
  Object.entries(smallMultipleData).forEach(([concept, dataPoints]) => {
    filteredSmallMultipleData[concept] = dataPoints.filter((dp) =>
      selectedLabels.includes(dp.date)
    );
  });

  // Here we no longer compute global max, as our SmallMultiple now fixes the Y-axis domain to [-1, 1].

  if (loading) {
    return (
      <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    );
  }

  return (
    <div
      className="sentiment-over-time"
      style={{
        height: widget.layout.h * 39 - 150,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div style={{ flex: "1 1 auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", padding: "0.5rem" }}>
          {Object.keys(filteredSmallMultipleData).map((concept, idx) => {
            const data = filteredSmallMultipleData[concept];
            const colorPalette = [
              "#A5D8FF",
              "#FFC9A8",
              "#FDFFA1",
              "#D2FF99",
              "#E6CCFF",
              "#FFB0C9",
              "#99FFEE",
              "#D5FF99",
              "#FFF59B",
              "#C4C4C4",
            ];
            const color = conceptColors[concept] || colorPalette[idx % colorPalette.length];
            return (
              <div
                key={concept}
                style={{
                  flex: "0 0 356px",
                  height: "220px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <SmallMultiple
                  data={data}
                  metric={concept}
                  color={color}
                  title={concept}
                  smoothLine={smoothLine}
                  maxYAxis={1} // Not used inside SmallMultiple now since YAxis domain is fixed
                />
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ flex: "0 0 auto", padding: "0.5rem 1rem" }}>
        {allLabels.length > 0 && (
          <LabelSelector
            labels={allLabels}
            selectedLabels={selectedLabels}
            onToggleLabel={handleToggleLabel}
          />
        )}
      </div>
    </div>
  );
}

export default SentimentOverTimeLineGraph;
